import { PathEditor } from '../curves/PathEditor.js';
import type { ShapeEditorProps } from './doc-context.js';
import { Bool, bool, Num, num, patch, Section } from './fields.js';
import { points3 } from './profile-source.js';

/** `tube`: a round tube along a 3D path. */
export function TubeEditor({ shape, onChange }: ShapeEditorProps) {
  const set = (key: string, v: unknown) => onChange(patch(shape, key, v));
  const path = points3(shape.path);
  const radius = num(shape.radius) ?? 0.1;
  const radiusEnd = num(shape.radiusEnd) ?? radius;
  return (
    <>
      <Section title="Path" hint="Path points (part space); the tube follows them in order.">
        <PathEditor
          points={path}
          closed={bool(shape.closed) ?? false}
          smooth={bool(shape.smooth) ?? false}
          samples={num(shape.samples) ?? 6}
          radii={path.map((_, i) => radius + (radiusEnd - radius) * (path.length > 1 ? i / (path.length - 1) : 0))}
          onChange={(p) => set('path', p)}
        />
      </Section>
      <Num label="Radius" hint="Tube radius (m)." min={0} value={num(shape.radius)} fallback={0.1} onChange={(v) => set('radius', v)} />
      <Num
        label="Radius end"
        hint="Radius at the last path point (linearly interpolated). Defaults to radius."
        optional
        min={0}
        value={num(shape.radiusEnd)}
        onChange={(v) => set('radiusEnd', v)}
      />
      <Num label="Segments" hint="Sides around the tube." integer min={3} value={num(shape.segments)} fallback={8} onChange={(v) => set('segments', v)} />
      <Bool label="Closed" hint="Connect the last path point back to the first (a ring)." value={bool(shape.closed)} onChange={(v) => set('closed', v)} />
      <Bool label="Caps" hint="Close the open ends." value={bool(shape.caps)} fallback onChange={(v) => set('caps', v)} />
      <Bool label="Smooth" hint="Smooth curve through the path points instead of straight segments." value={bool(shape.smooth)} onChange={(v) => set('smooth', v)} />
      <Num label="Samples" hint="Curve samples per path span when smooth." integer min={1} max={64} value={num(shape.samples)} fallback={6} onChange={(v) => set('samples', v)} />
    </>
  );
}
